// Compact-reveal abbreviation.
// Shrinks GPT's verbose date/zodiac/weekday output so it never wraps past
// one line on a narrow phone. Operates on the canonical English format
// produced by the backend prompts:
//   "Wednesday, 28 April 1999 · Taurus"  ->  "We, 28A99, Ta"
// Multi-question separators ("|") are preserved so reveals like
//   "80235 | Wednesday, 28 April 1999 · Taurus | Paris"
// become
//   "80235 | We, 28A99, Ta | Paris".

const WEEKDAY_ABBR = {
  monday: "Mo", tuesday: "Tu", wednesday: "We", thursday: "Th",
  friday: "Fr", saturday: "Sa", sunday: "Su",
};
const MONTH_ABBR = {
  january:   "Ja", february:  "Fe", march:     "Mr", april:     "Ap",
  may:       "My", june:      "Jn", july:      "Jl", august:    "Au",
  september: "Se", october:   "Oc", november:  "No", december:  "De",
};
const ZODIAC_ABBR = {
  aries: "Ar", taurus: "Ta", gemini: "Ge", cancer: "Cn",
  leo:   "Le", virgo:  "Vi", libra:  "Li", scorpio: "Sc",
  sagittarius: "Sg", capricorn: "Cp", aquarius: "Aq", pisces: "Pi",
};

/**
 * Reduce the verbose date+zodiac line to a compact one-liner.
 * Operates piece-by-piece on " | "-separated segments so multi-question
 * answers keep their delimiters intact.
 */
export function abbreviateReveal(text) {
  if (!text) return text;
  return text
    .split("|")
    .map((seg) => abbreviateSegment(seg.trim()))
    .join(" | ");
}

function abbreviateSegment(seg) {
  if (!seg) return seg;
  // Pattern: "Weekday, D Month YYYY · Zodiac"  (zodiac optional)
  const re = /^([A-Za-z]+),\s*(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})(?:\s*[·,-]\s*([A-Za-z]+))?\s*$/;
  const m = seg.match(re);
  if (m) {
    const [, wd, day, mo, yr, zod] = m;
    const wdAbbr  = WEEKDAY_ABBR[wd.toLowerCase()]  || wd.slice(0, 2);
    const moAbbr  = (MONTH_ABBR[mo.toLowerCase()]   || mo.slice(0, 1)).charAt(0).toUpperCase();
    const yrAbbr  = yr.slice(-2);
    const zodAbbr = zod ? (ZODIAC_ABBR[zod.toLowerCase()] || zod.slice(0, 2)) : "";
    const base    = `${wdAbbr}, ${day}${moAbbr}${yrAbbr}`;
    return zodAbbr ? `${base}, ${zodAbbr}` : base;
  }
  // Standalone "Weekday" -> 2-char
  const wdOnly = WEEKDAY_ABBR[seg.toLowerCase()];
  if (wdOnly) return wdOnly;
  // Standalone "Zodiac" -> 2-char
  const zodOnly = ZODIAC_ABBR[seg.toLowerCase()];
  if (zodOnly) return zodOnly;
  return seg;
}
